import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Zap, CheckCircle2 } from "lucide-react";

const VOLUMES = [15000, 60000, 150000, 400000, 800000];
const EMPTY = { company: "", contact_name: "", email: "", phone: "", segment: "", product_interest: "", estimated_volume: 0, region: "DACH", notes: "" };

export default function TradeFair() {
  const { t, plOptions } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...EMPTY });
  const [fair, setFair] = useState("Labelexpo Europe 2026");
  const [count, setCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/leads", { ...form, source: "Trade Fair", trade_fair: fair, status: "New", engagement: 15 });
      setCount((c) => c + 1);
      toast.success(`${t("lead_saved")}: ${form.company} ✓`);
      setForm({ ...EMPTY });
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg mx-auto space-y-4" data-testid="tradefair-page">
      <button onClick={() => navigate("/leads")} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 transition-colors duration-150" data-testid="back-to-leads">
        <ArrowLeft className="h-4 w-4" />{t("leads")}
      </button>

      <div className="bg-[#1B2A4A] rounded-lg p-5 text-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#F5A623] flex items-center justify-center">
            <Zap className="h-5 w-5 text-[#1B2A4A]" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: "Work Sans" }}>{t("tradefair")}</h1>
            <p className="text-xs text-white/60">{t("quick_capture")}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold font-mono text-[#F5A623]" data-testid="tradefair-counter">{count}</p>
          <p className="text-[10px] uppercase tracking-widest text-white/50">{t("captured_leads")}</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs text-gray-600">{t("trade_fair")}</Label>
          <Input data-testid="tf-fair-input" className="h-11" value={fair} onChange={(e) => setFair(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-gray-600">{t("company")} *</Label>
          <Input data-testid="tf-company-input" className="h-12 text-base" autoFocus value={form.company} onChange={(e) => set("company", e.target.value)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">{t("contact")}</Label>
            <Input data-testid="tf-contact-input" className="h-11" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">{t("phone")}</Label>
            <Input data-testid="tf-phone-input" className="h-11" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-gray-600">{t("email")}</Label>
          <Input data-testid="tf-email-input" className="h-11" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">{t("segment")}</Label>
            <Select value={form.segment} onValueChange={(v) => set("segment", v)}>
              <SelectTrigger className="h-11" data-testid="tf-segment-select"><SelectValue placeholder="–" /></SelectTrigger>
              <SelectContent>{plOptions("segments").map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">{t("product_interest")}</Label>
            <Select value={form.product_interest} onValueChange={(v) => set("product_interest", v)}>
              <SelectTrigger className="h-11" data-testid="tf-product-select"><SelectValue placeholder="–" /></SelectTrigger>
              <SelectContent>{plOptions("product_types").map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-gray-600">{t("estimated_volume")} (€)</Label>
          <div className="flex flex-wrap gap-2">
            {VOLUMES.map((v) => (
              <button key={v} data-testid={`tf-volume-${v}`} onClick={() => set("estimated_volume", v)}
                className={`px-3 py-2 rounded-full text-sm font-mono border transition-colors duration-150 ${
                  form.estimated_volume === v ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>
                {v.toLocaleString("de-DE")}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-gray-600">{t("notes")}</Label>
          <Textarea data-testid="tf-notes-input" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>
        <Button data-testid="tf-save-btn" disabled={saving || !form.company} onClick={save}
          className="w-full h-12 bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-bold text-base rounded-full">
          <CheckCircle2 className="h-5 w-5 mr-2" />{t("save_next")}
        </Button>
      </div>
    </div>
  );
}
